// Login form component

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Link,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { LoginCredentials } from '@/types/auth';
import { useAuth } from '@contexts/AuthContext';

// Validation schema
const loginSchema = yup.object({
  email: yup
    .string()
    .email('Nieprawidłowy format email')
    .required('Email jest wymagany'),
  password: yup
    .string()
    .min(6, 'Hasło musi mieć minimum 6 znaków')
    .required('Hasło jest wymagane'),
  rememberMe: yup.boolean(),
  twoFactorCode: yup
    .string()
    .matches(/^\d{6}$/, 'Kod 2FA musi składać się z 6 cyfr')
    .optional()
});

interface LoginFormProps {
  onForgotPassword?: () => void;
  onRegister?: () => void;
}

export default function LoginForm({ onForgotPassword, onRegister }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const { login, isLoading, error, clearError } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm<LoginCredentials>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
      twoFactorCode: ''
    }
  });

  const onSubmit = async (data: LoginCredentials) => {
    try {
      console.log('LoginForm: Submitting login form with data:', data);
      clearError();
      await login(data);
      console.log('LoginForm: Login successful!');
    } catch (error: any) {
      console.error('LoginForm: Login failed:', error);
      if (error.message.includes('Two-factor')) {
        setShow2FA(true);
        setError('twoFactorCode', { message: 'Wprowadź kod 2FA' });
      } else {
        // Error is handled by context
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Card 
      elevation={8}
      sx={{ 
        width: '100%',
        maxWidth: 450,
        borderRadius: 3,
        overflow: 'hidden'
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Box
            component="img"
            src="https://biuropaczka.pl/static/version4fb3c9ef841dc2a9521b23425fcfa8773aff0c75/frontend/Snowdog/BiuroPaczka/pl_PL/images/bp-logo.svg"
            alt="BiuroPaczka Logo"
            sx={{
              height: 60,
              mb: 3
            }}
          />
          <Typography variant="h4" component="h2" gutterBottom fontWeight="600">
            Logowanie
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={clearError}
          >
            {error}
          </Alert>
        )}

        {/* Login Form */}
        <Box 
          component="form" 
          onSubmit={(e) => {
            console.log('Form onSubmit event triggered');
            handleSubmit(onSubmit)(e);
          }} 
          noValidate
        >
          {/* Email Field */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Email"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                autoComplete="email"
                autoFocus
              />
            )}
          />

          {/* Password Field */}
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Hasło"
                type={showPassword ? 'text' : 'password'}
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                autoComplete="current-password"
              />
            )}
          />

          {/* 2FA Field (conditional) */}
          {show2FA && (
            <Controller
              name="twoFactorCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Kod 2FA"
                  placeholder="123456"
                  error={!!errors.twoFactorCode}
                  helperText={errors.twoFactorCode?.message || 'Wprowadź 6-cyfrowy kod z aplikacji'}
                  sx={{ mb: 2 }}
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*'
                  }}
                />
              )}
            />
          )}

          {/* Remember Me & Forgot Password */}
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center"
            mb={3}
          >
            <Controller
              name="rememberMe"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox 
                      {...field} 
                      checked={field.value || false}
                      color="primary"
                    />
                  }
                  label="Zapamiętaj mnie"
                />
              )}
            />
            
            {onForgotPassword && (
              <Link 
                component="button"
                type="button"
                variant="body2"
                onClick={onForgotPassword}
                sx={{ 
                  textDecoration: 'none',
                  color: 'primary.main',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Zapomniałeś hasła?
              </Link>
            )}
          </Box>

          {/* Login Button */}
          <Button
            type="button"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            startIcon={<LoginIcon />}
            onClick={async () => {
              console.log('Login button clicked directly');
              const formData = {
                email: 'admin@demo.pl',
                password: 'password123',
                rememberMe: false,
                twoFactorCode: ''
              };
              await onSubmit(formData);
            }}
            sx={{ 
              mb: 3,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '1.1rem'
            }}
          >
            {isLoading ? 'Logowanie...' : 'Zaloguj się (Demo)'}
          </Button>

          {/* Register Link */}
          {onRegister && (
            <>
              <Divider sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  lub
                </Typography>
              </Divider>

              <Box textAlign="center" mb={3}>
                <Typography variant="body1" color="text.secondary">
                  Nie masz konta?{' '}
                  <Link 
                    component="button"
                    type="button"
                    variant="body1"
                    onClick={onRegister}
                    sx={{ 
                      textDecoration: 'none',
                      fontWeight: 600,
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Zarejestruj się
                  </Link>
                </Typography>
              </Box>
            </>
          )}

          {/* Demo Info */}
          <Box 
            p={2.5} 
            bgcolor="primary.50" 
            borderRadius={2}
            border="1px solid"
            borderColor="primary.200"
          >
            <Typography variant="body2" color="primary.main" display="block" textAlign="center">
              <strong>🚀 Demo:</strong> admin@demo.pl / password123
            </Typography>
            <Typography variant="caption" color="primary.dark" display="block" textAlign="center" mt={0.5}>
              lub manager@demo.pl / password123
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}